import type { Company } from '../types/company';

export interface CompanyTreeNode {
  company: Company;
  children: CompanyTreeNode[];
}

function compareByName(a: Company, b: Company): number {
  return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
}

export function buildCompanyTree(companies: Company[]): CompanyTreeNode[] {
  const byId = new Map<string, Company>();
  const childrenByParent = new Map<string, Company[]>();
  const roots: Company[] = [];

  for (const company of companies) {
    byId.set(company.id, company);
  }

  for (const company of companies) {
    const parentId = typeof company.parentCompanyId === 'string' && company.parentCompanyId.trim().length > 0
      ? company.parentCompanyId
      : null;
    if (!parentId || !byId.has(parentId)) {
      roots.push(company);
      continue;
    }
    const rows = childrenByParent.get(parentId) ?? [];
    rows.push(company);
    childrenByParent.set(parentId, rows);
  }

  const seen = new Set<string>();
  const visit = (company: Company): CompanyTreeNode => {
    if (seen.has(company.id)) {
      return { company, children: [] };
    }
    seen.add(company.id);
    const children = (childrenByParent.get(company.id) ?? [])
      .sort(compareByName)
      .map((child) => visit(child));
    return { company, children };
  };

  return roots.sort(compareByName).map((root) => visit(root));
}

/** Ancestor chain from root → company (inclusive). */
export function getCompanyBreadcrumb(companies: Company[], companyId: string): Company[] {
  const byId = new Map(companies.map((c) => [c.id, c]));
  const path: Company[] = [];
  let current = byId.get(companyId);
  const guard = new Set<string>();
  while (current && !guard.has(current.id)) {
    guard.add(current.id);
    path.unshift(current);
    const parentId =
      typeof current.parentCompanyId === 'string' && current.parentCompanyId.trim().length > 0
        ? current.parentCompanyId
        : null;
    current = parentId ? byId.get(parentId) : undefined;
  }
  return path;
}

/** When multiple orgs share a name, append a short id suffix for disambiguation. */
export function buildCompanyDisplayLabels(companies: Company[]): Map<string, string> {
  const nameCounts = new Map<string, number>();
  for (const company of companies) {
    nameCounts.set(company.name, (nameCounts.get(company.name) ?? 0) + 1);
  }
  const labels = new Map<string, string>();
  for (const company of companies) {
    if ((nameCounts.get(company.name) ?? 0) > 1) {
      labels.set(company.id, `${company.name} · ${company.id.slice(-6)}`);
    } else {
      labels.set(company.id, company.name);
    }
  }
  return labels;
}

export function collectDescendantCompanyIds(
  companies: Company[],
  companyId: string,
): Set<string> {
  const childrenByParent = new Map<string, string[]>();
  for (const company of companies) {
    const parentId = typeof company.parentCompanyId === 'string' && company.parentCompanyId.trim().length > 0
      ? company.parentCompanyId
      : null;
    if (!parentId) continue;
    const rows = childrenByParent.get(parentId) ?? [];
    rows.push(company.id);
    childrenByParent.set(parentId, rows);
  }

  const ids = new Set<string>();
  const stack = [companyId];
  while (stack.length > 0) {
    const next = stack.pop()!;
    if (ids.has(next)) continue;
    ids.add(next);
    stack.push(...(childrenByParent.get(next) ?? []));
  }

  // Belt-and-braces: also fold in any company whose root-to-self `path[]`
  // contains `companyId`. Company docs persist this ancestry array
  // (createCompany / setCompanyParent in functions/src/companies.ts), so
  // descendants stay discoverable even when the in-memory `parentCompanyId`
  // edges are sparse — e.g. a deep child whose intermediate parent doc was
  // not loaded into `companies`. Mirrors the `companyPath` check in
  // firestore.rules and never widens the set beyond the named subtree.
  for (const company of companies) {
    if (Array.isArray(company.path) && company.path.includes(companyId)) {
      ids.add(company.id);
    }
  }
  return ids;
}

/**
 * Frontend analogue of the `isInCompanyTree(companyId)` check in
 * firestore.rules. Returns true when `candidateCompanyId` is the scope
 * company itself OR one of its descendants. Used to scope site/report
 * filters to a parent org *and its sub-orgs* without widening access
 * beyond the named subtree.
 *
 * @param companies          Known company records (for descendant resolution).
 * @param scopeCompanyId     The org being viewed/filtered to (parent).
 * @param candidateCompanyId The owning company of a site/report.
 */
export function isInCompanyTree(
  companies: Company[],
  scopeCompanyId: string,
  candidateCompanyId: string | null | undefined,
): boolean {
  if (typeof candidateCompanyId !== 'string' || candidateCompanyId.trim().length === 0) {
    return false;
  }
  if (candidateCompanyId === scopeCompanyId) return true;
  return collectDescendantCompanyIds(companies, scopeCompanyId).has(candidateCompanyId);
}

/** True when `companyId` has at least one direct child company in `companies`. */
export function hasSubCompanies(companies: Company[], companyId: string): boolean {
  return companies.some(
    (c) => typeof c.parentCompanyId === 'string' && c.parentCompanyId === companyId,
  );
}
